import { isObject } from '@tryiris-ai/js-types';

/*
 * Transient-failure classification for the device-discovery command (ENG-4220).
 *
 * Discovery is a periodic, re-triggered scan: a transport hiccup on one pass is
 * benign because the next pass (useDiscovery/handleDiscovery re-fires on every
 * selector trigger) retries it. Production error tracking shows exactly three
 * transient chains reaching `discover()`:
 *
 *   1. Error('mutation request timed out')            — state-sync subordinate
 *      out-of-sync mutation-queue deadline (subordinate.ts).
 *   2. Error('failed to publish to Nats')
 *        └─ cause: Error('timeout')                   — newts router request
 *      deadline (newts/router.ts) wrapped by the subordinate's mutate catch.
 *   3. TypeError('Failed to fetch')                   — browser fetch network
 *      failure via mailroom-helper requestHttp (requester.ts); the same path's
 *      client-side deadline throws MailroomTimeoutError / DOMException
 *      TimeoutError|AbortError (AbortSignal.timeout).
 *
 * Structured shapes are preferred over message substrings where a structure
 * exists: a raw nats.ws request timeout is a NatsError carrying
 * `code === 'TIMEOUT'` (nats.ws ErrorCode.Timeout) — matched on `.code`, not
 * on its message. The message checks below exist only for the bare-Error
 * shapes above, which carry no code, and are compared case-insensitively —
 * the old case-sensitive `isCausedBy(cause, 'NatsError: TIMEOUT')` guard never
 * matched any real chain and was dead since PR #182.
 *
 * Per rule: code-comments-required + reference-rules-in-comments
 * (ai.knowledge/global).
 */

/**
 * Maximum cause-chain links inspected. Mirrors `MaxDepth` in
 * `@studio/js-utils/logger` so a cyclic `Error.cause` cannot hang the walk.
 */
const MaxDepth = 5;

/** nats.ws `ErrorCode.Timeout` — the structured request-timeout code. */
const NatsTimeoutCode = 'TIMEOUT';

/**
 * Structured timeout error names: DOMException from `AbortSignal.timeout`
 * (TimeoutError, or AbortError on some runtimes) and the mailroom-helper
 * client-side deadline (MailroomTimeoutError, requester.ts).
 */
const TransientErrorNames = new Set(['AbortError', 'MailroomTimeoutError', 'TimeoutError']);

/** @internal Classify one link of a cause chain. */
const isTransientLink = (link: object): boolean => {
  // Structured check first: raw nats.ws NatsError request timeout.
  if ('code' in link && link.code === NatsTimeoutCode) {
    return true;
  }

  if ('name' in link && typeof link.name === 'string' && TransientErrorNames.has(link.name)) {
    return true;
  }

  const message =
    'message' in link && typeof link.message === 'string'
      ? link.message.trim().toLowerCase()
      : undefined;

  if (!message) {
    return false;
  }

  return (
    // Newts router request deadline (newts/router.ts) — a bare Error('timeout').
    message === 'timeout' ||
    // State-sync out-of-sync mutation-queue deadline (subordinate.ts).
    message === 'mutation request timed out' ||
    // Browser fetch network failure (mailroom-helper requestHttp).
    message.includes('failed to fetch')
  );
};

/**
 * Indicates whether a discovery failure is a benign transient transport
 * failure (a timeout or a momentary network drop) that the next discovery
 * pass will retry, as opposed to a genuine failure that must surface.
 *
 * Note: `'failed to publish to Nats'` is deliberately NOT in the transient
 * set — that wrapper is only benign when its inner cause is itself a timeout
 * (which this walk finds); a publish failure wrapping anything else still
 * counts as genuine.
 *
 * @param cause - Error-like object caught by the discovery command.
 * @returns `true` when the failure is transient and safe to swallow.
 */
export const isTransientDiscoveryFailure = (cause: unknown): boolean => {
  let link: unknown = cause;

  for (let depth = 0; depth < MaxDepth && isObject(link); depth += 1) {
    if (isTransientLink(link)) {
      return true;
    }

    link = 'cause' in link ? (link as { cause: unknown }).cause : undefined;
  }

  return false;
};
